import React, { useState, useEffect } from "react";
import { Package, Truck, FileText, AlertTriangle, Eye, Plus, Check, AlertCircle } from "lucide-react";
import * as roldanApi from "../../../../../../ApiCalls/taktikApiCalls/roldanApi";
import "./PanelControl.scss";
import LogisticsDemoPanelMockup from "./LogisticsDemoPanelMockup";

const PanelControl = ({
  mantisBusinessId,
  mantisBusinessUserId,
  token,
  onRemesaSelect,
  onPreManifiestoSelect,
  onManifiestoSelect,
  onCreateRemesa,
  refreshTrigger,
  isLogisticsDemo = false,
}) => {
  const [remesas, setRemesas] = useState([]);
  const [preManifiestos, setPreManifiestos] = useState([]);
  const [manifiestos, setManifiestos] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    estado_asociacion: null,
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mantisBusinessId, refreshTrigger, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [remesasRes, preManifiestosRes, manifiestosRes, alertasRes] = await Promise.all([
        roldanApi.getRemesas(mantisBusinessId, token, { limit: 50, ...filters }),
        roldanApi.getPreManifiestos(mantisBusinessId, token, { limit: 50 }),
        roldanApi.getManifiestos(mantisBusinessId, token, { limit: 10 }),
        roldanApi.getAlertas(mantisBusinessId, token, { estado: "Abierta", vista: false, limit: 20 }),
      ]);

      if (remesasRes?.success) setRemesas(remesasRes.data || []);
      if (preManifiestosRes?.success) setPreManifiestos(preManifiestosRes.data || []);
      if (manifiestosRes?.success) {
        const list = [...(manifiestosRes.data || [])]
          .sort((a, b) => new Date(b.fecha_creacion_erp || b.manifiesto_id || 0) - new Date(a.fecha_creacion_erp || a.manifiesto_id || 0))
          .slice(0, 10);
        setManifiestos(list);
      }
      if (alertasRes?.success) {
        // Filter out seen alerts (vista = true) to ensure they don't appear
        const unseenAlertas = (alertasRes.data || []).filter(a => !a.vista);
        setAlertas(unseenAlertas);
      }
    } catch (error) {
      console.error("Error loading panel data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsSeen = async (alertaId, e) => {
    e.stopPropagation();
    try {
      const res = await roldanApi.markAlertaVista(mantisBusinessId, mantisBusinessUserId, token, alertaId);
      if (res?.success) {
        // Remove the alert from the list since it's now seen
        setAlertas(prevAlertas => prevAlertas.filter(alerta => alerta.alerta_id !== alertaId));
      }
    } catch (error) {
      console.error("Error marking alerta as seen:", error);
    }
  };

  const getEstadoBadgeClass = (estado) => {
    const estadoClasses = {
      "Recibida": "badge-blue",
      "Validando": "badge-yellow",
      "Error de Validación": "badge-red",
      "Validada": "badge-green",
      "Asignada a Pre-Manifiesto": "badge-purple",
      "Confirmada para Carga": "badge-orange",
      "Manifestada": "badge-gray",
    };
    return estadoClasses[estado] || "badge-gray";
  };

  const isFechaVencida = (fechaViaje) => {
    if (!fechaViaje) return false;
    const fecha = new Date(fechaViaje);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    fecha.setHours(0, 0, 0, 0);
    return fecha < hoy;
  };

  const isRemesaVencida = (remesa) => {
    if (remesa.estado !== "Recibida") return false;
    const fechaToCheck = remesa.fecha_cargue || remesa.fecha_creacion;
    return isFechaVencida(fechaToCheck);
  };

  if (!isLogisticsDemo && loading) {
    return <div className="panel-loading">Cargando...</div>;
  }

  return (
    <>
      {isLogisticsDemo && <LogisticsDemoPanelMockup />}
      {loading ? (
        <div className="panel-loading">Cargando...</div>
      ) : (
    <div className="panel-control">
      <div className="panel-header">
        <h1>Panel de Control</h1>
        <div className="filters">
          <select
            value={filters.estado_asociacion || ""}
            onChange={(e) => setFilters({ estado_asociacion: e.target.value || null })}
            className="filter-select"
          >
            <option value="">Todos los estados</option>
            <option value="Sin Asignar">Sin Asignar</option>
            <option value="En Pre-Manifiesto">En Pre-Manifiesto</option>
            <option value="Manifestadas">Manifestadas</option>
          </select>
        </div>
      </div>

      {/* Alertas Section - Only show if there are alerts */}
      {alertas.length > 0 && (
        <div className="alertas-section">
          <div className="section-header">
            <AlertTriangle size={20} />
            <h2>Alertas de Validación</h2>
            <span className="badge">{alertas.length}</span>
          </div>
          <div className="alertas-list">
            {alertas.map((alerta) => (
              <div
                key={alerta.alerta_id}
                className="alerta-item"
                onClick={() => onRemesaSelect(alerta.remesa_id)}
              >
                <div className="alerta-content">
                  <span className="alerta-numero">Remesa: {alerta.numero_remesa}</span>
                  <p className="alerta-mensaje">{alerta.mensaje_error}</p>
                  <span className="alerta-fecha">
                    {new Date(alerta.fecha_creacion).toLocaleDateString()}
                  </span>
                </div>
                <div className="alerta-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="mark-seen-btn"
                    onClick={(e) => handleMarkAsSeen(alerta.alerta_id, e)}
                    title="Marcar como vista"
                  >
                    <Check size={16} />
                  </button>
                  <Eye size={18} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Remesas Section */}
      <div className="remesas-section">
        <div className="section-header">
          <Package size={20} />
          <h2>Remesas Recientes</h2>
          <button className="create-btn" onClick={onCreateRemesa}>
            <Plus size={16} />
            Nueva Remesa
          </button>
        </div>
        <div className="remesas-grid">
          {remesas.slice(0, 12).map((remesa) => {
            const vencido = isRemesaVencida(remesa);
            return (
            <div
              key={remesa.remesa_id}
              className={`remesa-card ${remesa.estado === "Error de Validación" ? "error-card" : ""} ${vencido ? "vencido-card" : ""}`}
              onClick={() => onRemesaSelect(remesa.remesa_id)}
            >
              <div className="remesa-header">
                <div className="flex items-center gap-2">
                  <span className="remesa-numero">{remesa.numero_remesa}</span>
                  {vencido && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold text-xs bg-red-500 text-white">
                      <AlertCircle size={10} /> Vencido
                    </span>
                  )}
                </div>
                <span className={`estado-badge ${getEstadoBadgeClass(remesa.estado)}`}>
                  {remesa.estado}
                </span>
              </div>
              <div className="remesa-info">
                <p>
                  <strong>Cliente:</strong> {remesa.cliente || "N/A"}
                </p>
                {remesa.nombre_conductor && (
                  <p>
                    <strong>Conductor:</strong> {remesa.nombre_conductor}
                  </p>
                )}
                {remesa.origen && (
                  <p>
                    <strong>Origen:</strong> {remesa.origen}
                  </p>
                )}
                {remesa.destino && (
                  <p>
                    <strong>Destino:</strong> {remesa.destino}
                  </p>
                )}
                <p>
                  <strong>Fecha:</strong> {new Date(remesa.fecha_creacion).toLocaleDateString()}
                </p>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Pre-Manifiestos Section */}
      <div className="px-2">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="flex items-center justify-between gap-4 p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Truck size={20} />
              <h2 className="text-base font-bold text-gray-900">Pre-Manifiestos Abiertos</h2>
            </div>
          </div>
          <div className="grid gap-3 p-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {preManifiestos.filter(pm => pm.estado === "Abierto").slice(0, 6).map((pm) => {
              const vencido = isFechaVencida(pm.fecha_viaje);
              return (
              <button
                key={pm.pre_manifiesto_id}
                className={`text-left bg-gradient-to-b rounded-xl p-4 shadow-sm hover:shadow-md transition ${
                  vencido 
                    ? 'from-red-50 to-red-100 border border-red-300' 
                    : 'from-white to-gray-50 border border-gray-200'
                }`}
                onClick={() => onPreManifiestoSelect(pm.pre_manifiesto_id)}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-extrabold text-gray-900">{pm.numero_pre_manifiesto}</span>
                    {vencido && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold text-xs bg-red-500 text-white">
                        <AlertCircle size={10} /> Vencido
                      </span>
                    )}
                  </div>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full font-semibold text-xs bg-sky-100 text-sky-800">{pm.cantidad_remesas} remesas</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm text-gray-800">
                  <p className={vencido ? 'text-red-700 font-semibold' : ''}>
                    <span className="text-gray-500 font-semibold">Fecha Viaje:</span> {new Date(pm.fecha_viaje).toLocaleDateString()}
                  </p>
                  {pm.origen && (
                    <p><span className="text-gray-500 font-semibold">Origen:</span> {pm.origen}</p>
                  )}
                  {pm.destino && (
                    <p><span className="text-gray-500 font-semibold">Destino:</span> {pm.destino}</p>
                  )}
                </div>
              </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Manifiestos recientes Section */}
      <div className="px-2 mt-4">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
          <div className="flex items-center justify-between gap-4 p-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <FileText size={20} />
              <h2 className="text-base font-bold text-gray-900">Manifiestos recientes</h2>
            </div>
          </div>
          <div className="grid gap-3 p-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {manifiestos.map((m) => (
              <button
                key={m.manifiesto_id}
                type="button"
                className="text-left bg-gradient-to-b from-white to-gray-50 border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md hover:border-blue-200 transition"
                onClick={() => onManifiestoSelect?.(m.manifiesto_id)}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono font-extrabold text-gray-900">{m.numero_manifiesto_erp || m.manifiesto_id}</span>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full font-semibold text-xs ${
                    (m.estado || "").toLowerCase().includes("activo") ? "bg-emerald-100 text-emerald-700" :
                    (m.estado || "").toLowerCase().includes("anulado") ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"
                  }`}>
                    {m.estado || "N/A"}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-2 text-sm text-gray-800">
                  {m.autorizacion != null && String(m.autorizacion).trim() !== "" && (
                    <p><span className="text-gray-500 font-semibold">RNDC:</span> <span className="text-emerald-700 font-medium">{String(m.autorizacion).trim()}</span></p>
                  )}
                  <p><span className="text-gray-500 font-semibold">Fecha:</span> {m.fecha_creacion_erp ? new Date(m.fecha_creacion_erp).toLocaleDateString() : "N/A"}</p>
                  {m.remesa_numeros != null && String(m.remesa_numeros).trim() !== "" && (
                    <p className="col-span-full"><span className="text-gray-500 font-semibold">Remesas:</span> <span className="font-mono text-gray-900">{m.remesa_numeros}</span></p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
      )}
    </>
  );
};

export default PanelControl;

